/* eslint-disable no-unused-vars */
import {
  useElement,
  useState,
  useStaleLayout,
  useRect,
  useEffect,
  useModel,
} from '@nebula.js/stardust';
import picassojs from 'picasso.js';
import picassoQ from 'picasso-plugin-q';
import picassoHammer from 'picasso-plugin-hammer';
import * as d3 from 'd3';

export default function supernova() {
  const picasso = picassojs();
  picasso.use(picassoQ);
  picasso.use(picassoHammer);

  return {
    qae: {
      properties: {
        qHyperCubeDef: {
          qDimensions: [],
          qMeasures: [],
          qInitialDataFetch: [{ qWidth: 2, qHeight: 5000 }],
          qSuppressZero: false,
          qSuppressMissing: true,
        },
        showTitles: true,
        title: '',
        subtitle: '',
        footnote: '',
      },
      data: {
        targets: [
          {
            path: '/qHyperCubeDef',
            dimensions: {
              min: 1,
              max: 1,
            },
            measures: {
              min: 1,
              max: 1,
            },
          },
        ],
      },
    },
    component() {
      const element = useElement();
      const layout = useStaleLayout();
      const rect = useRect();
      const model = useModel();

      const [instance, setInstance] = useState();

      useEffect(() => {
        const p = picasso.chart({
          element,
          data: [],
          settings: {},
        });

        setInstance(p);

        return () => {
          p.destroy();
        };
      }, []);

      useEffect(() => {
        if (!instance) {
          return;
        }

        const colorScale = d3.scaleOrdinal()
        .domain(['United States', 'United Kingdom', 'Australia', 'France', 'Germany', 'Japan'])
        .range(['#655dc6',
        '#1c355e',
        '#006580',
        '#009845',
        '#870064',
        '#27b9ed',
        '#244b5a']);

        instance.update({
          data: [
            {
              type: 'q',
              key: 'qHyperCube',
              data: layout.qHyperCube,
            },
          ],
          settings: {
            scales: {
              color: {
                data: {
                  extract: {
                    field: 'qDimensionInfo/0',
                  },
                },
                type: 'color',
              },
            },
            components: [
              {
                key: 'donut',
                type: 'pie',
                data: {
                  extract: {
                    field: 'qDimensionInfo/0',
                    props: {
                      num: {
                        field: 'qMeasureInfo/0',
                      },
                    },
                  },
                },
                settings: {
                  slice: {
                    cornerRadius: 0,
                    innerRadius: 0.4,
                    arc: {
                      ref: 'num',
                    },
                    fill: (d) => colorScale(d.datum.label),
                  },
                },
              },
              {
                type: 'labels',
                displayOrder: 3,
                settings: {
                  sources: [
                    {
                      component: 'donut',
                      selector: 'path',
                      strategy: {
                        type: 'slice',
                        settings: {
                          direction: 'horizontal',
                          fontSize: 10,
                          align: 0.5,
                          justify: 1,
                          labels: [
                            {
                              label: (d) => d.data.num.label,
                              placements: [
                                { position: 'into', fill: '#fff' },
                              ],
                            },
                            {
                              label: (d) => d.data.label,
                              placements: [
                                { position: 'outside', fill: '#000' },
                              ],
                            },
                          ],
                        },
                      },
                    },
                  ],
                },
              },
            ],
            interactions: [
              {
                type: 'hammer',
                gestures: [
                  {
                    type: 'Tap',
                    events: {
                      tap(e) {
                        const chartBounds = element.getBoundingClientRect();
                        const cx = e.center.x - chartBounds.left;
                        const cy = e.center.y - chartBounds.top;
                        const shapes = this.chart.shapesAt({ x: cx, y: cy });
                        const values = shapes.map((shape) => shape.data.value);
                        model.selectHyperCubeValues('/qHyperCubeDef', 0, values, true);
                      },
                    },
                  },
                ],
              },
            ],
          },
        });
      }, [layout, instance, rect.width]);

      useEffect(() => {
        if (!instance) {
          return;
        }
        instance.update();
      }, [rect.width, rect.height, instance]);
    },
  };
}
